import { useMemo, useState } from 'react';
import { isFinal, isLive, type Game } from '../lib/api';
import { useAllGames } from '../lib/useGames';

/** Suggestions built from this week's real schedule, so nothing asks about a game that is not being played. */
function suggestions(games: Game[]): string[] {
  const name = (g: Game, side: 'home' | 'away') => (side === 'home' ? g.home_team : g.away_team).name;
  const matchup = (g: Game) => `${name(g, 'away')} at ${name(g, 'home')}`;
  const rank = (g: Game) => Math.min(g.home_team.rank ?? 99, g.away_team.rank ?? 99);
  const upcoming = games.filter((g) => !isFinal(g) && !isLive(g)).sort((a, b) => rank(a) - rank(b) || new Date(a.date).getTime() - new Date(b.date).getTime());
  const finals = games.filter(isFinal).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const nfl = upcoming.find((g) => (g.league ?? 'nfl') === 'nfl');
  const cfb = upcoming.find((g) => g.league === 'cfb');
  const out: string[] = [];
  if (nfl) out.push(`Who wins ${matchup(nfl)}`);
  if (cfb ?? upcoming[1]) out.push(`Best bet for ${matchup((cfb ?? upcoming[1]) as Game)}`);
  if (finals[0]) out.push(`Why did ${matchup(finals[0])} turn out that way`);
  if (upcoming[2] ?? cfb ?? nfl) out.push(`Storylines for ${matchup((upcoming[2] ?? cfb ?? nfl) as Game)}`);
  return out.length ? out : ['Who wins the next NFL game', 'Best bet this week', 'Why did the last game turn out that way'];
}

export function AskKova({ onAsk, loading = false, examples = true, onBlue = false }: { onAsk: (q: string) => void; loading?: boolean; examples?: boolean; onBlue?: boolean }) {
  const [q, setQ] = useState('');
  const { games } = useAllGames();
  const chips = useMemo(() => suggestions(games), [games]);
  const submit = (text: string) => {
    const t = text.trim();
    if (!t || loading) return;
    onAsk(t);
    setQ('');
  };
  return (
    <div className="w-full">
      <form onSubmit={(e) => { e.preventDefault(); submit(q); }} className={`flex overflow-hidden rounded-sm bg-paper ${onBlue ? '' : 'border border-ink/25'} focus-within:ring-2 focus-within:ring-blue`}>
        <input
          id="ask-kova"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          // a placeholder vanishes as soon as you type, so it is not a label
          aria-label="Ask about any NFL or college game"
          placeholder="Ask about any NFL or college game"
          disabled={loading}
          className="h-12 min-w-0 flex-1 bg-transparent px-4 text-[15px] text-ink outline-none placeholder:text-faint disabled:opacity-60 md:h-14 md:text-[16px]"
          autoComplete="off"
        />
        <button type="submit" disabled={loading || !q.trim()} className="btn-primary h-12 shrink-0 rounded-none px-6 md:h-14">Ask</button>
      </form>
      {examples && (
        <div className="scroll-x mt-3 md:flex-wrap">
          {chips.map((e) => <button key={e} onClick={() => submit(e)} className={`chip shrink-0 ${onBlue ? 'border-paper/30 bg-paper/10 text-paper hover:border-paper hover:bg-paper hover:text-blue' : ''}`}>{e}</button>)}
        </div>
      )}
    </div>
  );
}
